<template>
  <section class="carousel-section" aria-labelledby="catalog-heading">
    <header class="head">
      <h2 id="catalog-heading" class="subtitle">Productos de temporada</h2>
      <p class="text">
        Una selección de las piezas que estamos produciendo ahora. Cada una se
        puede adaptar a tus necesidades en tamaño, material y acabado; el
        catálogo completo reúne todo lo demás, por categorías.
      </p>
      <router-link class="catalog-link" to="/print3d/catalogo">
        Ver catálogo completo
      </router-link>
    </header>

    <div v-if="loading" class="state-msg">Cargando catálogo…</div>
    <div v-else-if="error" class="state-msg state-error" role="alert">
      No fue posible cargar el catálogo. Intenta recargar la página.
    </div>

    <!-- Sin destacados marcados en el panel el carrusel quedaría vacío: se
         invita al catálogo general, que siempre tiene todo el inventario. -->
    <div v-else-if="!products.length" class="state-msg empty-featured">
      <p>Aún no hay piezas destacadas esta temporada.</p>
      <router-link class="catalog-link" to="/print3d/catalogo">
        Ver catálogo completo
      </router-link>
    </div>

    <div v-else class="carousel-shell">
      <div
        class="carousel-wrap"
        role="region"
        aria-roledescription="carousel"
        aria-label="Galería de productos 3D"
      >
        <button
          type="button"
          class="nav-btn nav-prev"
          aria-label="Anterior"
          @click="scrollByCard(-1)"
        >
          ‹
        </button>

        <div ref="track" class="track" tabindex="0" @keydown="onKey" @scroll.passive="onScroll">
          <ProductCard
            v-for="product in products"
            :key="product.id"
            :product="product"
            :material-name="materialNameFor(product.materialId)"
            @order-clicked="onOrderClicked"
          />
        </div>

        <button
          type="button"
          class="nav-btn nav-next"
          aria-label="Siguiente"
          @click="scrollByCard(1)"
        >
          ›
        </button>
      </div>

      <!-- Indicadores: dan noción de cuántas piezas hay y permiten saltar. -->
      <ol v-if="products.length > 1" class="dots">
        <li v-for="(product, index) in products" :key="product.id">
          <button
            type="button"
            class="dot"
            :class="{ 'is-active': index === activeIndex }"
            :aria-label="`Ver ${product.name}`"
            :aria-current="index === activeIndex ? 'true' : 'false'"
            @click="goTo(index)"
          ></button>
        </li>
      </ol>

      <p class="swipe-hint">Desliza, usa las flechas ‹ › o el teclado para recorrer los destacados.</p>

      <router-link class="catalog-link catalog-link-footer" to="/print3d/catalogo">
        Ver catálogo completo
      </router-link>
    </div>

    <p v-if="orderConfirmation" class="ok-msg" role="status">{{ orderConfirmation }}</p>

    <CatalogOrderModal
      v-if="showOrderModal && selectedProduct"
      ref="orderModal"
      :product="selectedProduct"
      @confirm="onConfirmOrder"
      @close="showOrderModal = false"
    />
  </section>
</template>

<script>
import ProductCard from './ProductCard.vue';
import CatalogOrderModal from './CatalogOrderModal.vue';
import { getProducts, getMaterials, submitCatalogOrder } from '@/services/print3dService';

export default {
  name: 'CarouselSection',
  components: { ProductCard, CatalogOrderModal },
  data() {
    return {
      products: [],
      materials: [],
      loading: true,
      error: null,
      selectedProduct: null,
      showOrderModal: false,
      orderConfirmation: '',
      activeIndex: 0,
    };
  },
  async mounted() {
    try {
      // Sólo los productos de temporada: el resto vive en /print3d/catalogo.
      const [{ products }, { materials }] = await Promise.all([
        getProducts({ featured: true }),
        getMaterials(),
      ]);
      this.products = products;
      this.materials = materials;
    } catch (e) {
      this.error = e;
    } finally {
      this.loading = false;
    }
  },
  methods: {
    materialNameFor(id) {
      const found = this.materials.find((m) => m.id === id);
      return found ? found.displayName : 'Por definir';
    },
    scrollByCard(direction) {
      const track = this.$refs.track;
      if (!track) return;
      const card = track.querySelector('.product-card');
      const step = card ? card.offsetWidth + 16 : 280;
      track.scrollBy({ left: direction * step, behavior: 'smooth' });
    },
    // Mantiene sincronizados los indicadores con el desplazamiento (incluido
    // el swipe táctil, que usa el scroll nativo del track).
    onScroll() {
      const track = this.$refs.track;
      if (!track) return;
      const card = track.querySelector('.product-card');
      const step = card ? card.offsetWidth + 16 : 280;
      const index = Math.round(track.scrollLeft / step);
      this.activeIndex = Math.min(Math.max(index, 0), this.products.length - 1);
    },
    goTo(index) {
      const track = this.$refs.track;
      const card = track?.children?.[index];
      if (!track || !card) return;
      track.scrollTo({ left: card.offsetLeft - track.offsetLeft, behavior: 'smooth' });
    },
    onKey(e) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.scrollByCard(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.scrollByCard(-1);
      }
    },
    onOrderClicked(product) {
      this.selectedProduct = product;
      this.orderConfirmation = '';
      this.showOrderModal = true;
    },
    async onConfirmOrder(orderPayload) {
      const { receipt, error } = await submitCatalogOrder(orderPayload);
      if (error) {
        this.$refs.orderModal?.setError(error);
        return;
      }
      this.orderConfirmation = `Pedido recibido (#${receipt.id}). Te contactaremos al correo ${orderPayload.contactEmail}.`;
      this.showOrderModal = false;
      this.selectedProduct = null;
    },
  },
};
</script>

<style scoped>
.carousel-section {
  width: 100%;
  margin: 0 auto;
}

.head {
  margin-bottom: 1.5rem;
}

.head .subtitle {
  margin-bottom: 0.5rem;
}

.head .text {
  max-width: 720px;
  margin: 0;
}

.state-msg {
  padding: 2rem;
  text-align: center;
  color: #4F5665;
}

.empty-featured {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.9rem;
}

.empty-featured p {
  margin: 0;
}

/* Puente al catálogo general: se repite arriba y abajo porque el carrusel es
   largo y el visitante decide explorar en cualquiera de los dos momentos. */
.catalog-link {
  display: inline-block;
  margin-top: 0.9rem;
  padding: 0.5rem 1.1rem;
  border: 1px solid var(--color-action, #526D82);
  border-radius: 8px;
  background: white;
  color: var(--color-action, #526D82);
  font-family: var(--font-body, "Jost", sans-serif);
  font-size: 0.95rem;
  text-decoration: none;
  transition: background 0.2s, color 0.2s;
}

.catalog-link:hover {
  background: var(--color-action, #526D82);
  color: white;
}

.catalog-link:focus-visible {
  outline: 2px solid #3D566B;
  outline-offset: 2px;
}

.catalog-link-footer {
  align-self: center;
  margin-top: 0.4rem;
}

.state-error {
  color: #B91C1C;
}

.ok-msg {
  background: rgba(22, 163, 74, 0.1);
  color: #166534;
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  margin: 0.5rem 0 0;
  font-size: 0.9rem;
}

.carousel-shell {
  display: flex;
  flex-direction: column;
}

.carousel-wrap {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.dots {
  list-style: none;
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin: 0.4rem 0 0;
  padding: 0;
}

.dot {
  width: 10px;
  height: 10px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid #9DB2BF;
  background: white;
  cursor: pointer;
  transition: background 0.2s, transform 0.2s;
}

.dot.is-active {
  background: #2E8A99;
  border-color: #2E8A99;
  transform: scale(1.2);
}

.dot:focus-visible {
  outline: 2px solid #526D82;
  outline-offset: 2px;
}

.swipe-hint {
  text-align: center;
  font-family: "Jost", sans-serif;
  font-size: 0.85rem;
  color: #4F5665;
  margin: 0.6rem 0 0;
}

.track {
  display: flex;
  gap: 16px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-padding: 0 1rem;
  padding: 1rem 0.5rem;
  flex: 1;
  scrollbar-width: thin;
}

.track:focus-visible {
  outline: 2px solid #526D82;
  outline-offset: 4px;
  border-radius: 8px;
}

.track > :deep(.product-card) {
  scroll-snap-align: start;
  flex: 0 0 auto;
}

.nav-btn {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid #9DB2BF;
  background: white;
  color: #526D82;
  font-size: 1.6rem;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s, color 0.2s, transform 0.2s;
}

.nav-btn:hover {
  background: #526D82;
  color: white;
  transform: translateY(-1px);
}

.nav-btn:focus-visible {
  outline: 2px solid #3D566B;
  outline-offset: 2px;
}

@media (max-width: 800px) {
  .nav-btn {
    width: 36px;
    height: 36px;
    font-size: 1.3rem;
  }
}
</style>
